import React, { useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import FinalizeTaskDialog from '../../FinalizeTaskController/FinalizeTaskDialog'
import { genericOpinionSchema } from '../GenericOpinionTask/GenericOpinionSchema'

import useSubmitTask from 'hooks/useSubmitTask'

import TaskHeaderController from 'controllers/TaskManager/TaskSubControllers/TaskHeader/TaskHeader'
import AlertBox from 'components/AlertBox/AlertBox'

import styles from '../../Dialog.module.sass'
import TaskMenuController from '../../TaskMenu/TaskMenuController'
import InfoList from 'components/InfoList/InfoList'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import HFTextInput from 'components/HFInputs/HFTextInput'
import { useFetchList } from 'store'
import MedicalOpinion from 'models/MedicalOpinion'
import { mapOpinionTaskDetails } from 'controllers/TaskManager/TaskSubControllers/TaskDetailsControllers/detailsUtils'
import { useReload } from 'store'

const ManagementOpinionTaskController = (props) => {
	const { data, dismiss, id, resolve } = props
	const { i18n } = useGlobal()
	const reload = useReload()

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(genericOpinionSchema),
		shouldUnregister: true,
	})
	const { handleSubmit, formState, reset, getValues } = methods

	const { error, submitting, approveTask } = useSubmitTask(
		data?.id,
		formState?.errors
	)

	const opinionUri = `/metadata/medical_opinion/${data?.opinionRequest?.opinionId}`

	const { contents } = useFetchList(
		[data?.opinionRequest?.opinionId && opinionUri],
		[data?.opinionRequest?.opinionId && MedicalOpinion.fromJSON]
	)

	const [medicalOpinion] = contents

	const details = mapOpinionTaskDetails(medicalOpinion, i18n)

	const onSubmit = (data) => {
		approveTask(data, 'submit')
			.then(() => {
				resolve()
				reload([
					`clinical_processes/${data?.processId}/management_opinions`,
					`/clinical_processes/${data?.processId}/tasks`,
					`/medical_opinions/management_opinions`,
				])
			})
			.catch(() => {})
	}

	const customError = error?.map((err) => {
		return {
			title: i18n`Error`,
			text: err,
		}
	})

	useEffect(() => {
		if (
			data.formState &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			reset && data.formState && reset(data.formState)
		}
	}, [reset, data.formState, formState.isDirty, formState.submitCount])

	return (
		<>
			{submitting && (
				<FinalizeTaskDialog
					dismiss={dismiss}
					submitting={submitting}
					task={data}
				/>
			)}
			<FormProvider {...methods}>
				<div className={styles['form-container']}>
					<TaskHeaderController task={data} />
					<form
						id={`form_${id}`}
						className={styles['form']}
						onSubmit={handleSubmit(onSubmit)}
					>
						{customError && customError.length > 0 && (
							<AlertBox
								className={styles['alert-box']}
								type='error'
								items={customError}
							/>
						)}
						<InfoList items={details} />
						<fieldset>
							<VStack className={styles['observation-container']}>
								<h5>{i18n`Administrative opinion`}</h5>
								<HFTextInput
									name='opinion'
									placeholder={i18n`Write your administrative observation`}
									multiline={true}
									rows={5}
									noResize
								/>
							</VStack>
						</fieldset>
					</form>
				</div>
			</FormProvider>
			<TaskMenuController
				data={{
					formId: id,
					options: ['postpone', 'forward'],
					submitting,
					submissionType: 'submit',
					getValues,
				}}
			/>
		</>
	)
}

export default ManagementOpinionTaskController
